import React, { useState, useEffect } from "react";

const DecimalToHexStep = () => {
  const [decimalInputValue, setDecimalInputValue] = useState("254");
  const [originalNumber, setOriginalNumber] = useState(254);
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [remainders, setRemainders] = useState([]);
  const [activityVisible, setActivityVisible] = useState(false);
  const [finalVisible, setFinalVisible] = useState(false);
  const [qInput, setQInput] = useState("");
  const [rInput, setRInput] = useState("");
  const [hInput, setHInput] = useState("");
  const [userHex, setUserHex] = useState("");
  const [feedback, setFeedback] = useState("");
  const [correctHex, setCorrectHex] = useState("");
  const [completedStepsDisplay, setCompletedStepsDisplay] = useState([]);

  const styles = {
    body: {
      fontFamily: "'Press Start 2P', cursive, monospace",
      background: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
      minHeight: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      padding: "40px",
      position: "relative",
      overflow: "hidden",
      color: "#fff",
    },
    backgroundShapes: {
      position: "absolute",
      width: "100%",
      height: "100%",
      top: 0,
      left: 0,
      zIndex: 0,
      backgroundImage: `
        repeating-linear-gradient(45deg, rgba(255,0,255,0.1) 0 10px, transparent 10px 20px),
        repeating-linear-gradient(-45deg, rgba(0,255,255,0.1) 0 10px, transparent 10px 20px),
        radial-gradient(circle at 20% 30%, rgba(255,255,0,0.1) 0 50px, transparent 50px 100%),
        radial-gradient(circle at 80% 70%, rgba(0,255,0,0.08) 0 50px, transparent 50px 100%)`,
      backgroundRepeat: "repeat",
      animation: "moveBg 60s linear infinite",
    },
    card: {
      width: "100%",
      maxWidth: 700,
      background: "rgba(0,0,0,0.8)",
      borderRadius: 20,
      boxShadow: "0 0 30px #ff00ff, 0 0 60px #00ffff",
      padding: 28,
      zIndex: 1,
      border: "2px solid #ff00ff",
    },
    h1: {
      fontSize: 26,
      fontWeight: 700,
      textAlign: "center",
      color: "#00ffff",
      textShadow: "2px 2px #ff00ff",
      marginBottom: 10,
    },
    meta: { textAlign: "center", fontSize: 14, color: "#ccc", marginBottom: 20 },
    row: {
      display: "flex",
      gap: 12,
      alignItems: "center",
      flexWrap: "wrap",
      justifyContent: "center",
      marginBottom: 14,
      zIndex: 2,
    },
    label: { fontWeight: 700, color: "#ff00ff", fontSize: 14 },
    input: {
      padding: "10px 14px",
      borderRadius: 8,
      border: "2px solid #00ffff",
      fontSize: 14,
      width: 130,
      background: "#111",
      color: "#fff",
      textAlign: "center",
      outline: "none",
    },
    button: {
      background: "linear-gradient(135deg, #ff00ff, #00ffff)",
      color: "#111",
      border: "none",
      padding: "10px 18px",
      borderRadius: 10,
      cursor: "pointer",
      fontWeight: 700,
      fontSize: 14,
      textShadow: "1px 1px #000",
      transition: "0.3s",
    },
    secondaryBtn: {
      background: "linear-gradient(135deg, #ffcc00, #ff0066)",
      color: "#111",
      border: "none",
      padding: "10px 18px",
      borderRadius: 10,
      cursor: "pointer",
      fontWeight: 700,
      fontSize: 14,
    },
    stepLine: {
      background: "#111",
      border: "2px dashed #ff00ff",
      padding: "12px 16px",
      marginBottom: 10,
      borderRadius: 12,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      color: "#fff",
    },
    resultHex: {
      fontFamily: "monospace",
      fontSize: 20,
      padding: "8px 14px",
      borderRadius: 12,
      border: "2px solid #00ffff",
      background: "#111",
      display: "inline-block",
      marginTop: 8,
      fontWeight: 700,
      color: "#ff00ff",
    },
    feedbackOk: { color: "#00ffcc", fontWeight: 700, marginTop: 10 },
    feedbackBad: { color: "#ff3366", fontWeight: 700, marginTop: 10 },
    "@keyframes moveBg": {
      "0%": { backgroundPosition: "0 0, 0 0, 0 0, 0 0" },
      "100%": { backgroundPosition: "1000px 1000px, -1000px 1000px, 500px -500px, -500px -500px" },
    },
  };

  function decimalToHexDigit(n) {
    if (n < 10) return n.toString();
    return String.fromCharCode(55 + n);
  }

  function computeSteps(n) {
    const arr = [];
    let value = n;
    if (value === 0) {
      arr.push({ value: 0, quotient: 0, remainder: 0 });
      return arr;
    }
    while (value > 0) {
      const q = Math.floor(value / 16);
      const r = value % 16;
      arr.push({ value, quotient: q, remainder: r });
      value = q;
    }
    return arr;
  }

  const start = () => {
    const parsed = parseInt(decimalInputValue, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      alert("Enter a valid number!");
      return;
    }
    setOriginalNumber(parsed);
    setSteps(computeSteps(parsed));
    setStepIndex(0);
    setRemainders([]);
    setCompletedStepsDisplay([]);
    setActivityVisible(true);
    setFinalVisible(false);
    setUserHex("");
    setFeedback("");
    setCorrectHex("");
    setQInput(""); setRInput(""); setHInput("");
  };

  const submitCurrentStep = () => {
    setFeedback("");
    const expected = steps[stepIndex];
    if (!expected) return;
    if (qInput.trim() === "" || rInput.trim() === "") {
      setFeedback("Enter both quotient and remainder!");
      return;
    }
    const qNum = Number(qInput);
    const rNum = Number(rInput);
    const expectedHex = decimalToHexDigit(expected.remainder);
    const hexVal = hInput ? hInput.trim().toUpperCase() : null;

    if (qNum === expected.quotient && rNum === expected.remainder && (!hexVal || hexVal === expectedHex)) {
      setRemainders((prev) => [...prev, hexVal || rNum.toString()]);
      setCompletedStepsDisplay((prev) => [...prev, { value: expected.value, quotient: qNum, remainder: hexVal || rNum.toString() }]);
      setQInput(""); setRInput(""); setHInput("");
      const next = stepIndex + 1;
      if (next < steps.length) setStepIndex(next);
      else setFinalVisible(true);
    } else setFeedback("Incorrect! Try again.");
  };

  const handleFinalCheck = () => {
  setFeedback("");
  const user = userHex.trim().toUpperCase();
  if (!user) {
    setFeedback("Enter a hexadecimal number!");
    return;
  }
  const correct = remainders.slice().reverse().join("");
  const correctHexFinal = originalNumber === 0 ? "0" : correct;

  if (user === correctHexFinal) {
    setFeedback("✅ Correct!");
    setCorrectHex(correctHexFinal);
  } else {
    setFeedback("❌ Not quite, read bottom-up.");
    setCorrectHex("");
  }
};


  const showSolution = () => {
    const correct = remainders.slice().reverse().join("");
    const correctHexFinal = originalNumber === 0 ? "0" : correct;
    setCorrectHex(correctHexFinal);
    setFeedback("Solution revealed!");
  };

  useEffect(() => { setQInput(""); setRInput(""); setHInput(""); }, [stepIndex]);

  return (
    <div style={styles.body}>
      <div style={styles.backgroundShapes}></div>
      <div style={styles.card}>
        <h1 style={styles.h1}>Decimal → Hexadecimal (Retro 90s)</h1>
        <div style={styles.meta}>Convert decimals with neon 90s retro vibes!</div>

        <div style={styles.row}>
          <label style={styles.label}>Decimal:</label>
          <input style={styles.input} type="number" min="0" value={decimalInputValue} onChange={(e) => setDecimalInputValue(e.target.value)} />
          <button style={styles.button} onClick={start}>Start</button>
          <button style={styles.secondaryBtn} onClick={() => setDecimalInputValue("254")}>Reset</button>
        </div>

        {activityVisible && !finalVisible && steps[stepIndex] && (
          <div style={styles.stepLine}>
            <div>Step {stepIndex+1}: {steps[stepIndex].value} ÷ 16 = ? R ?</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input placeholder="quotient" value={qInput} onChange={(e)=>setQInput(e.target.value)} style={{...styles.input,width:110}}/>
              <input placeholder="remainder" value={rInput} onChange={(e)=>setRInput(e.target.value)} style={{...styles.input,width:90}}/>
              {steps[stepIndex].remainder>=10 && <input placeholder="Hex (A-F)" value={hInput} onChange={(e)=>setHInput(e.target.value.toUpperCase().slice(0,1))} style={{...styles.input,width:90}} maxLength={1}/>}
              <button style={styles.button} onClick={submitCurrentStep}>Submit Step</button>
            </div>
          </div>
        )}

        {completedStepsDisplay.length>0 && (
          <div style={{marginTop:12}}>
            <div style={{fontWeight:700}}>Completed steps</div>
            {completedStepsDisplay.map((s,i)=>(
              <div key={i} style={styles.stepLine}>
                <div><strong>{s.value} ÷ 16</strong></div>
                <div>= {s.quotient} R {s.remainder}</div>
              </div>
            ))}
          </div>
        )}

        {finalVisible && (
          <div style={{marginTop:18, textAlign:"center"}}>
            <p>Enter final hexadecimal (read bottom-up)</p>
            <input type="text" placeholder="e.g. FE" value={userHex} onChange={(e)=>setUserHex(e.target.value.toUpperCase())} style={{...styles.input,width:160}}/>
            <div style={{display:"flex",gap:12,justifyContent:"center",marginTop:12}}>
              <button style={styles.button} onClick={handleFinalCheck}>Check</button>
              <button style={styles.secondaryBtn} onClick={showSolution}>Show Solution</button>
            </div>
            {feedback && <div style={feedback.startsWith("✅") ? styles.feedbackOk : styles.feedbackBad}>{feedback}</div>}
            {correctHex && <div style={{marginTop:12}}><div style={{fontSize:12}}>Correct hexadecimal:</div><div style={styles.resultHex}>{correctHex}</div></div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default DecimalToHexStep;
